import { profilesLocalStorageKey } from './environment';
import userAccountToStoredProfile from '../server/utils/userAccountToStoredProfile';
import { setupStorage, useLocalStorage } from './hooks/useLocalStorage';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { isSubProfile } from './typeGuards';
import { logger } from '../common/logger';
import {
  fetchProfileCookie,
  fetchSubAccounts,
} from '../service/user-profile-api';
import { loginFetchClient as getLoginFetch } from '../service/loginFetchClient';
import { LoggedInUser, Profile, ProfileCookies } from '../common/types';
import { yleLoginCookieKey } from '../common/utils';
import { useEventListener } from './hooks/useEventListener';
import usePromiseWithErrorHandling from './hooks/usePromiseWithErrorHandling';

export const ProfilesLocalStorageProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  // For useLocalStorage-hook only.
  const [profilesState, setProfilesState] = useState<Profile[]>(
    setupStorage(profilesLocalStorageKey, [])
  );

  const {
    getItem: getProfilesLocalStorage, // Since local storage might contain legacy profilesState, only use this to pass to the profilesState useMemo
    setItem: setProfileLocalStorage, // Use this to update profilesState in local storage
    eventKey,
  } = useLocalStorage<Profile[]>({ key: profilesLocalStorageKey });

  useEventListener(
    eventKey,
    () => {
      const currentLocalStorageProfiles = getProfilesLocalStorage();
      setProfilesState(currentLocalStorageProfiles || []);
    },
    window
  );

  const loginFetch = useMemo(() => getLoginFetch(), []);

  const setProfile = useCallback(
    (profile: Profile) => {
      const currentLocalStorageProfiles = getProfilesLocalStorage() || [];
      const profileIndex = currentLocalStorageProfiles.findIndex(
        p => p.userId === profile.userId
      );
      if (profileIndex === -1) {
        setProfileLocalStorage(
          currentLocalStorageProfiles.concat(profile).sort(profileSortComparer)
        );
      } else {
        const newProfiles = currentLocalStorageProfiles.slice();
        newProfiles.splice(profileIndex, 1, profile).sort(profileSortComparer);
        setProfileLocalStorage(newProfiles);
      }
    },
    [getProfilesLocalStorage, setProfileLocalStorage]
  );

  const deleteProfileByUserId = useCallback(
    (userId: string) => {
      const profileIds = profilesState
        .filter(p => p.parentId === userId)
        .map(p => p.userId)
        .concat(userId);

      setProfileLocalStorage(
        profilesState
          .filter(p => !profileIds.includes(p.userId))
          .sort(profileSortComparer)
      );
    },
    [profilesState, setProfileLocalStorage]
  );

  const deleteProfileByCookie = useCallback(
    (cookie: string) => {
      const foundProfile = profilesState.find(p => p.cookie === cookie);
      if (foundProfile) {
        deleteProfileByUserId(foundProfile.userId);
      }
    },
    [deleteProfileByUserId, profilesState]
  );

  const fetchSubProfilesAsync = useCallback(async () => {
    const profiles = getProfilesLocalStorage() || [];
    const cookies = new Set(
      profiles
        .filter(p => !isSubProfile(p))
        .map(p => p.cookie)
        .filter((cookie): cookie is string => cookie !== undefined)
    );

    const subProfiles = await fetchSubAccounts(loginFetch, Array.from(cookies));

    const profileIdsToRefresh = profiles
      .filter(isSubProfile)
      .map(p => p.userId);

    setProfileLocalStorage(
      profiles
        .filter(p => !profileIdsToRefresh.includes(p.userId))
        .concat(subProfiles)
        .sort(profileSortComparer)
    );
    //eslint-disable-next-line
  }, [loginFetch, setProfileLocalStorage]);

  const { run: fetchSubProfiles } = usePromiseWithErrorHandling(
    fetchSubProfilesAsync
  );

  const setProfileByUserAsync = useCallback(
    async (user: LoggedInUser, cookie?: string) => {
      if (cookie) {
        try {
          setProfile(userAccountToStoredProfile(user, cookie));
          return;
        } catch {}
      }

      // if cookie is missing, attempting to use current user's cookie
      try {
        const fetchedCookie = (await fetchProfileCookie(loginFetch))[
          yleLoginCookieKey
        ];

        if (!fetchedCookie) {
          logger.debug('User cookie missing');
          return;
        }
        const profile = userAccountToStoredProfile(user, fetchedCookie);

        setProfile(profile);
        fetchSubProfiles();
      } catch (e) {
        logger.error(
          { error: e },
          'Failed to get profile data during setting user to profilesState local storage'
        );
      }
    },
    [loginFetch, setProfile, fetchSubProfiles]
  );

  const { run: setProfileByUser } = usePromiseWithErrorHandling(
    setProfileByUserAsync
  );

  return (
    <ProfilesLocalStorageContext.Provider
      value={{
        deleteProfileByUserId,
        deleteProfileByCookie,
        fetchSubProfiles,
        setProfileByUser,
        setProfile,
        profiles: profilesState || [],
      }}
    >
      {children}
    </ProfilesLocalStorageContext.Provider>
  );
};

export function useProfilesLocalStorage(): Value {
  return useContext(ProfilesLocalStorageContext);
}

type Value = {
  deleteProfileByUserId: (userId: string) => void;
  deleteProfileByCookie: (cookie: string) => void;
  setProfile: (profile: Profile) => void;
  profiles: Profile[];
  setProfileByUser: (user: LoggedInUser, cookie?: string) => void;
  fetchSubProfiles: () => void;
};

const ProfilesLocalStorageContext = React.createContext<Value>({
  deleteProfileByUserId() {
    throw new Error("Profiles local storage context doesn't have a Provider");
  },
  deleteProfileByCookie() {
    throw new Error("Profiles local storage context doesn't have a Provider");
  },
  setProfileByUser() {
    throw new Error("Profiles local storage context doesn't have a Provider");
  },
  profiles: [],
  setProfile() {
    throw new Error("Profiles local storage context doesn't have a Provider");
  },
  fetchSubProfiles() {
    throw new Error("Profiles local storage context doesn't have a Provider");
  },
});

function profileSortComparer(profileA: Profile, profileB: Profile): number {
  const [idA, isParentA] = isSubProfile(profileA)
    ? [profileA.parentId, false]
    : [profileA.userId, true];
  const [idB, isParentB] = isSubProfile(profileB)
    ? [profileB.parentId, false]
    : [profileB.userId, true];

  if (idA === undefined || idB === undefined) {
    logger.warn(
      { profileA, profileB },
      `Profiles includes profiles without userIds`
    );

    if (idA === undefined && idB === undefined) {
      return 0;
    } else if (idA === undefined) {
      return -1;
    } else {
      return 1;
    }
  }

  if (idA !== idB) return idA.localeCompare(idB);

  if (isParentA && isParentB) {
    return 0;
  } else if (!isParentA) {
    return 1;
  } else {
    return -1;
  }
}

/**
 * Exported only for testing
 */
export type LegacyProfile = {
  userInfo: LoggedInUser;
  cookies: ProfileCookies;
};

/**
 * Picks a color index between 0 and 5
 * @param userId
 * @param allProfiles
 */
export function getParentProfileColorIndex(
  userId: string,
  allProfiles: Profile[]
) {
  return (
    Math.max(
      0,
      allProfiles
        .filter(p => !isSubProfile(p))
        .findIndex(p => p.userId === userId)
    ) % 6
  );
}
